import { UserActionTypes, useUser } from '@myosh/myosh-login';
import {
  AboutInfoModal,
  Button,
  Dialog,
  DomElementAlignment,
  DomTargetPosition,
  IconButton,
  OdinIcon,
  type OdinIconName,
  OdinIconSize,
  OverlayPanel,
  ToggleButton,
  useScreenSize,
} from '@myosh/odin-components';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import useIsMetadataKey from '../../../hooks/use-is-metadata-key';
import useProfileData from '../../../hooks/use-profile-data';
import useUserSchemas from '../../../hooks/use-user-schemas';
import { useAppDispatch } from '../../../redux/hooks';
import {
  api,
  useGetCurrentSchemaInfoQuery,
  useGetFeatureToursQuery,
  useGetHelpMetadataQuery,
} from '../../../redux/services/api';
import { setIsManualSchemaUpdate } from '../../../redux/slices/schema';
import { logout, terminateDashboardSession } from '../../../utils/auth-util';
import { resolveUserValue } from '../../../utils/user-functions';
import UserSettingsModal from '../../admin/user/user-settings-modal.component';
import useForceOdinAdmin from '../hooks/use-force-odin-admin';
import AvatarButton from './avatar-button.component';
import SettingsMenuFeatureTour from './feature-tour-guides/settings-menu-tour';
import SchemaSelector, { type SchemaDropdownItem } from './schema-selector.component';

type ProfileNavItem = {
  name: string;
  action?: () => void;
  authorized?: boolean;
  icon?: OdinIconName;
  className?: string;
  visible: boolean;
};

/**
 * Delay (in ms) to ensure OverlayPanel fully unmounts before opening a modal.
 * This prevents conflicts with the browser's top-layer stacking context where both
 * the closing overlay and opening modal compete for the top layer. The delay allows
 * the overlay's exit animation and cleanup to complete before the modal attempts to
 * claim the top layer via showModal().
 *
 * Note: OverlayPanel default transition is 400ms, but unmount begins partway through
 * the exit animation, making 150ms sufficient for the cleanup phase.
 *
 * ⚠️ COUPLING WARNING: This value is tightly coupled to OverlayPanel's internal
 * transition timing. If OverlayPanel's transition duration changes, this delay
 * may need adjustment.
 *
 * TODO: Consider one of these improvements to decouple from OverlayPanel internals:
 * 1. Expose transition duration from OverlayPanel for dynamic calculation
 * 2. Add an onUnmountComplete callback to OverlayPanel
 * 3. Use a more conservative delay (e.g., 500ms) to avoid timing issues
 */
const OVERLAY_UNMOUNT_DELAY = 150;

function ProfileMenu() {
  const { state: userState, dispatch: userDispatch } = useUser();
  const { schemas, currentSchema, isControllingSuperuser } = useUserSchemas(userState);
  const hasSchemas = Boolean(schemas && schemas.length > 1);
  const { data: helpMetadata } = useGetHelpMetadataQuery();

  const { profileData: { user: userData } = {} } = useProfileData();
  const isSuperUser = userData?.superUser;
  const isPasswordChangeRequired = userData?.passwordChangeRequired;

  const { odinAdminOverrideEnabled, setOdinAdminOverrideEnabled } = useForceOdinAdmin();
  const isOdinAdmin = useIsMetadataKey('display_odin_admin');
  const shouldDisplayAdminOverride = !isOdinAdmin && isSuperUser;

  const [selectSchema, setSelectSchema] = useState(currentSchema);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAboutInfoOpen, setAboutInfoOpen] = useState(false);
  const [isSelectSchemaOpen, setSelectSchemaOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  const menuButtonRef = useRef(null);

  const { isMobile } = useScreenSize();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { data: schemaInfo } = useGetCurrentSchemaInfoQuery();
  const hasLogoutButton = Boolean(schemaInfo?.hide_logout_button && schemaInfo?.hide_logout_button !== 'true');

  const { pathname } = useLocation();
  const isRecordOpen = pathname.includes('/records');

  const { data: featureTourData } = useGetFeatureToursQuery();

  const updateCurrentSchema = (schema: SchemaDropdownItem) => {
    setSelectSchema(schema);
  };

  const submitCurrentSchemaSelection = useCallback(async () => {
    setSelectSchemaOpen(false);

    if (selectSchema) {
      await terminateDashboardSession(userState.user?.currentSchema);
      dispatch(setIsManualSchemaUpdate(true));
      userDispatch({ type: UserActionTypes.UpdateCurrentSchema, payload: selectSchema.value });
      dispatch(api.util.resetApiState());
      window.location.replace('/');
    }
  }, [selectSchema, userState.user?.currentSchema]);

  const onMenuIconClick = useCallback(() => {
    setIsMenuOpen((isMenuOpen) => !isMenuOpen);
  }, []);

  const onHideMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const onMenuItemClick = useCallback((action?: () => void) => {
    setIsMenuOpen(false);
    // Defer modal opening until after OverlayPanel unmounts
    // This prevents dialog top-layer conflicts
    if (action) {
      setTimeout(() => {
        action();
      }, OVERLAY_UNMOUNT_DELAY);
    }
  }, []);

  const handleLogout = useCallback(
    async () => await logout(userState.user?.currentSchema),
    [userState.user?.currentSchema]
  );
  const openAboutInfo = useCallback(() => {
    setAboutInfoOpen(true);
  }, []);
  const openSelectSchema = useCallback(() => setSelectSchemaOpen(true), []);
  const openSettingsModal = useCallback(() => {
    setIsSettingsModalOpen(true);
  }, []);

  const items: Array<ProfileNavItem> = useMemo(
    () =>
      (
        [
          {
            name: t('about'),
            action: openAboutInfo,
            icon: 'Info',
            className: 'about-menu-item', // about-menu-item is used for Feature Tours
            visible: true,
          },
          {
            name: isSuperUser ? t('select-schema', 'Select Schema') : t('select-company', 'Select Company'),
            action: openSelectSchema,
            authorized: hasSchemas,
            icon: 'ListIndentDecrease',
            visible: true,
          },
          {
            name: t('settings'),
            action: openSettingsModal,
            authorized: !isSuperUser,
            icon: 'Settings2',
            className: 'settings-menu-item', // settings-menu-item is used for Feature Tours
            visible: true,
          },
          {
            name: t('log-out'),
            action: handleLogout,
            icon: 'LogOut',
            className: 'logout-menu-item', // logout-menu-item is used for Feature Tours
            visible: hasLogoutButton,
          },
        ] satisfies Array<ProfileNavItem>
      ).filter((item) => item.visible),
    [openAboutInfo, openSelectSchema, openSettingsModal, handleLogout, isSuperUser, hasSchemas, hasLogoutButton]
  );

  const schemaSelectorfooter = useMemo(() => {
    return (
      <div className="flex justify-end">
        <Button type="primary" onClick={submitCurrentSchemaSelection}>
          {isSuperUser ? `Change Schema` : `Change Company`}
        </Button>
      </div>
    );
  }, [isSuperUser, submitCurrentSchemaSelection]);

  const onHelpIconClick = () => {
    if (helpMetadata?.helpLink) {
      window.open(helpMetadata.helpLink, '_blank');
    }
  };

  return (
    <>
      <div className="flex max-h-full items-center justify-end gap-3 px-3 py-2">
        {helpMetadata?.enableHelp && (
          <IconButton
            icon="CircleQuestionMark"
            iconSize={OdinIconSize.Small}
            classNames="mx-2"
            onClick={onHelpIconClick}
          />
        )}
        {!isMobile && (
          <span className="cursor-pointer font-medium" onClick={onMenuIconClick}>
            {userData && resolveUserValue(userData)}
          </span>
        )}
        {/*settings-menu-button is used for Feature Tours*/}
        <AvatarButton ref={menuButtonRef} onClick={onMenuIconClick} />
      </div>
      <OverlayPanel
        visible={isMenuOpen}
        target={menuButtonRef.current}
        targetPosition={DomTargetPosition.BottomRight}
        elementAlignment={DomElementAlignment.TopRight}
        hidden={onHideMenu}
        className="min-w-[200px] rounded bg-mono-1"
        shouldCheckZIndex={featureTourData?.hasSettingsMenuTourRan}
        keepOverlayOpen={!featureTourData?.hasSettingsMenuTourRan}
      >
        <ul className="py-3">
          {isMobile && <li className="px-4 py-1 font-medium">{userData && resolveUserValue(userData)}</li>}
          {shouldDisplayAdminOverride && (
            <li className="flex items-center gap-3 px-3 py-1">
              <ToggleButton checked={odinAdminOverrideEnabled} onChange={setOdinAdminOverrideEnabled} />
              <span>Odin Admin</span>
            </li>
          )}
          {items.map((menuItem) => {
            return (
              <li
                key={menuItem.name}
                onClick={() => onMenuItemClick(menuItem.action)}
                hidden={menuItem.authorized === false}
                className={`flex cursor-pointer gap-3 px-3 py-1 hover:bg-primary-2 hover:text-mono-1 ${menuItem.className ?? ''}`}
              >
                {menuItem.icon && <OdinIcon icon={menuItem.icon} size={OdinIconSize.Small} />}
                <span>{menuItem.name}</span>
              </li>
            );
          })}
        </ul>
      </OverlayPanel>
      <AboutInfoModal
        copyright={`${t('copyright')} ${new Date().getFullYear()}`}
        currentSchema={{ text: currentSchema?.text || 'n/a' }}
        isAboutInfoOpen={isAboutInfoOpen}
        onClose={() => setAboutInfoOpen(false)}
        showLogoutButton={false}
        schemaTimeZone={schemaInfo?.['time-zone']}
        {...schemaInfo}
        controllingSuperuser={isControllingSuperuser ? userState?.user?.preferred_username : undefined}
        version={process.env.REACT_APP_VERSION}
        showUserTimeZone
      />
      {isSelectSchemaOpen && (
        <Dialog
          key="schema-selector"
          header={isSuperUser ? t('select-schema', 'Select Schema') : t('select-company', 'Select Company')}
          containerClassName="sm:w-80"
          footer={schemaSelectorfooter}
          visible={isSelectSchemaOpen}
          onClose={() => setSelectSchemaOpen(false)}
        >
          {schemas && currentSchema && (
            <SchemaSelector updateCurrentSchema={updateCurrentSchema} schemas={schemas} currentSchema={currentSchema} />
          )}
        </Dialog>
      )}
      <UserSettingsModal
        userData={userData}
        visible={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
      />
      {!isSuperUser &&
        !isRecordOpen &&
        !isPasswordChangeRequired &&
        !userData?.ssoUser &&
        hasLogoutButton &&
        featureTourData &&
        !featureTourData.hasSettingsMenuTourRan && (
          <SettingsMenuFeatureTour
            isMenuOpen={isMenuOpen}
            featureTourData={featureTourData}
            setIsMenuOpen={setIsMenuOpen}
          />
        )}
    </>
  );
}

export default React.memo(ProfileMenu);
